import { PlusIcon } from '@heroicons/react/24/solid';
import { Button, Chip, Input, Spinner } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFetchSpecialtiesQuery } from '@/slices/specialties-api-slice.ts';
import type { SpecialtiesSelectFormControlProps } from '../types/form-props.type.ts';
import type { SpecialtyModel } from '../types/lookup.type.ts';

export const SpecialtiesSelect: FC<SpecialtiesSelectFormControlProps> = ({
  whiteListSpecialties,
  name,
  accountId,
  representativeId,
  onAdd,
  onRemove,
  title = 'Specialties',
}) => {
  const { data, isLoading } = useFetchSpecialtiesQuery(
    whiteListSpecialties?.length ? skipToken : undefined,
  );

  const { register, watch, setValue: setParentValue } = useFormContext();

  const [searchTerm, setSearchTerm] = useState('');
  const parentSpecialtiesWatch = (watch(name) as SpecialtyModel[]) || [];

  const addSpecialty = (specialty: Partial<SpecialtyModel>) => {
    const isAlreadyAdded = parentSpecialtiesWatch.some(
      s =>
        (specialty.specialtyId && s.specialtyId === specialty.specialtyId) ||
        s.description === specialty.description,
    );

    if (isAlreadyAdded) {
      console.info('Specialty already added');
      return;
    }

    const added = {
      ...specialty,
      accountId: accountId,
      representativeId: representativeId,
    };
    setParentValue(name, [...parentSpecialtiesWatch, added]);

    if (onAdd) {
      onAdd(added);
    }

    setSearchTerm('');
  };

  const removeSpecialty = (value: SpecialtyModel) => {
    const updated = parentSpecialtiesWatch.filter(x =>
      x.specialtyId ? x.specialtyId !== value.specialtyId : x.description !== value.description,
    );
    setParentValue(name, updated);

    if (onRemove) {
      onRemove(value);
    }
  };

  const availableSpecialties = (whiteListSpecialties ?? data ?? [])
    .filter(
      specialty =>
        specialty &&
        !parentSpecialtiesWatch.some(selected => selected.specialtyId === specialty.specialtyId),
    )
    .filter(specialty =>
      searchTerm.length
        ? specialty.description.toLowerCase().includes(searchTerm.toLowerCase())
        : true,
    )
    .sort((a, b) => a.description.localeCompare(b.description));

  const canCreateNew =
    searchTerm.length > 0 &&
    !availableSpecialties.some(s => s.description.toLowerCase() === searchTerm.toLowerCase()) &&
    !parentSpecialtiesWatch.some(s => s.description.toLowerCase() === searchTerm.toLowerCase());

  return (
    <div className='flex flex-col gap-3'>
      {!!title && <span className='text-sm font-medium text-gray-700'>{title}</span>}

      {parentSpecialtiesWatch.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {parentSpecialtiesWatch.map((specialty, index) => (
            <Chip
              key={specialty.specialtyId || `${specialty.description}-${index}`}
              color='primary'
              variant='flat'
              onClose={() => removeSpecialty(specialty)}
            >
              {specialty.description}
            </Chip>
          ))}
        </div>
      )}

      <Input
        type='text'
        placeholder='Search or add specialty...'
        variant='bordered'
        value={searchTerm}
        onValueChange={setSearchTerm}
        startContent={isLoading ? <Spinner size='sm' /> : null}
      />

      <div className='flex flex-col gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2'>
        {canCreateNew && (
          <Button
            size='sm'
            variant='light'
            color='success'
            startContent={<PlusIcon className='size-5' />}
            onPress={() => addSpecialty({ description: searchTerm })}
            className='justify-start'
          >
            {searchTerm}
          </Button>
        )}
        {availableSpecialties.length > 0 && (
          <>
            <p className='text-xs text-gray-500 px-2 pt-1'>Select from existing:</p>
            <div className='flex gap-2 flex-wrap'>
              {availableSpecialties.map(specialty => (
                <Button
                  key={specialty.specialtyId}
                  size='sm'
                  variant='light'
                  color='primary'
                  startContent={<PlusIcon className='size-4' />}
                  onPress={() => addSpecialty(specialty)}
                  className='justify-start'
                >
                  {specialty.description}
                </Button>
              ))}
            </div>
          </>
        )}

        {!isLoading && availableSpecialties.length === 0 && !canCreateNew && (
          <p className='text-sm text-gray-500 text-center py-2'>No specialties found</p>
        )}
      </div>

      <input type='hidden' {...register(name)} />
    </div>
  );
};
